import { useCallback, useState } from 'react';
import { usePanelWidth } from '../PanelResize';

interface Options<T> {
  /** `localStorage` key the chosen width persists under, one per surface. */
  widthStorageKey: string;
  defaultView?: T;
}

/**
 * Pairs the persisted width with the (deliberately unpersisted) open view, so
 * a host wires a `DetailSidebar` and its toggles from one call.
 */
export function useDetailSidebar<T extends string>({ widthStorageKey, defaultView }: Options<T>) {
  const { width, setWidth, commitWidth } = usePanelWidth(widthStorageKey);
  const [view, setView] = useState<T | undefined>(defaultView);

  const toggle = useCallback(
    (type: T) => {
      if (view === type) {
        setView(undefined);
      } else {
        setView(type);
      }
    },
    [view],
  );

  // Unconditional, unlike `toggle`: a host that must surface a view (starting a
  // comment needs the composer) would otherwise close it when it is already up.
  const openView = useCallback((type: T) => setView(type), []);

  return { view, toggle, openView, width, setWidth, commitWidth };
}
